import React, { useMemo } from 'react';
import i18n from '../../../../../i18n';
import { fmt } from '../../../data/metrics';
import type { DrillPanel } from '../../../types';
import { fmtRankingValue, PROV_LABEL } from '../DrillPanels';
import { DrillTile } from './DrillTile';
import { DrillBarChart } from './charts/DrillBarChart';
import { shortOutletLabels } from './outletNames';

export function DrillRankingSection({
  panel,
  sectionId,
}: {
  metricKey: string;
  panel: Extract<DrillPanel, { kind: 'ranking' }>;
  sectionId: string;
}) {
  const max = Math.max(...panel.rows.map(r => Math.abs(r.value)), 0.0001);

  const inspector = useMemo(
    () => (
      <div className="dcrux-rank-list">
        {panel.rows.map((r, i) => {
          const cmpDelta =
            r.compareValue != null && r.compareValue !== 0
              ? +(((r.value - r.compareValue) / r.compareValue) * 100).toFixed(1)
              : null;
          return (
          <React.Fragment key={r.id}>
            <div className="dcrux-rank-row dcrux-rank-row-adv">
              <span className="dcrux-outlet-rank">{i + 1}</span>
              <span className="dcrux-outlet-name" title={r.name}>
                {r.name}
              </span>
              <span className="dcrux-si-track">
                <span
                  className="dcrux-si-fill"
                  style={{
                    width: `${Math.max(2, (Math.abs(r.value) / max) * 100)}%`,
                  }}
                />
              </span>
              <span className="dcrux-outlet-val">
                {fmtRankingValue(panel.unit, r.value)}
                {r.compareValue != null ? (
                  <span
                    className="dcrux-cmp-chip"
                    title={i18n.t('performance:drillUi.vsComparePeriod')}
                  >
                    {fmtRankingValue(panel.unit, r.compareValue)}
                    {cmpDelta != null
                      ? ` · ${cmpDelta >= 0 ? '+' : ''}${cmpDelta}%`
                      : ''}
                  </span>
                ) : null}
              </span>
            </div>
            {r.sub ? <div className="dcrux-rank-sub">{r.sub}</div> : null}
          </React.Fragment>
          );
        })}
      </div>
    ),
    [panel, max],
  );

  const top = panel.rows.slice(0, 3);
  if (!top.length) return null;

  const prov =
    panel.provenance && PROV_LABEL[panel.provenance]
      ? PROV_LABEL[panel.provenance]
      : panel.provenance;
  // Strip the prefix all rows share ("L'Osteria München ") so six outlets of
  // one chain stay distinguishable; the full name rides in the tooltip.
  const labels = shortOutletLabels(panel.rows.map(r => r.name));
  const chartData = top.map((r, i) => ({
    name: labels[i],
    fullName: r.name,
    value: Math.abs(r.value),
  }));

  return (
    <DrillTile
      sectionId={sectionId}
      span={4}
      title={panel.title}
      meta={prov}
      inspector={inspector}
      n={
        panel.sample
          ? panel.sample.unit === 'items'
            ? i18n.t('performance:drillUi.nItems', { n: fmt.num(panel.sample.n) })
            : i18n.t('performance:drillUi.nReservations', {
                count: panel.sample.n,
                n: fmt.num(panel.sample.n),
              })
          : undefined
      }
    >
      <DrillBarChart
        data={chartData}
        fmtVal={v => fmtRankingValue(panel.unit, v)}
        height={Math.max(120, 36 + chartData.length * 28)}
        horizontal
        showValues
      />
    </DrillTile>
  );
}
